// ** Modules
import React from 'react';

// ** UI
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { visuallyHidden } from '@mui/utils';

// ** Components
import StrapiImageRenderer from '@/components/strapi/StrapiImageRenderer';
import ComponentSharedTextEditor from '@/components/strapi/Shared/ComponentSharedTextEditor';
import { getRankingLabel } from '@/features/ap-plus/mappers/mapCompetitionGrid';

// ** Types
import type { CompetitionCardModel, ImageEntry } from '@/components/common/CompetitionCard';

// ** Local
import MetaRow, { BODY_SM_DETAIL_SX } from './MetaRow';
import { getYouTubeId, isVideoEntry } from './videoUtils';

type CompetitionDetailMobileProps = {
	item: CompetitionCardModel;
	allImages: ImageEntry[];
	selectedImageIndex: number;
	onSelectImage: (index: number) => void;
};

const nullToUndefined = <T,>(value: T | null | undefined): T | undefined => (value === null ? undefined : value);

const CompetitionDetailMobile = ({
	item,
	allImages,
	selectedImageIndex,
	onSelectImage
}: CompetitionDetailMobileProps) => {
	const current = allImages[selectedImageIndex] ?? allImages[0];
	const touchStart = React.useRef<number | null>(null);
	const rankingLabel = getRankingLabel(item);

	const block = item?.detail?.fullDescription;

	const parsedContent = React.useMemo(() => {
		if (typeof block?.blockContent !== 'string') return null;

		try {
			return JSON.parse(block.blockContent);
		} catch (error) {
			console.error('Failed to parse Strapi blockContent JSON:', error);
			return null;
		}
	}, [block?.blockContent]);

	const handleImageTouchStart = React.useCallback((e: React.TouchEvent) => {
		touchStart.current = e.touches[0].clientX;
	}, []);

	const handleImageTouchEnd = React.useCallback(
		(e: React.TouchEvent) => {
			if (touchStart.current === null) return;
			const diff = touchStart.current - e.changedTouches[0].clientX;
			const threshold = 50;
			if (diff > threshold && selectedImageIndex < allImages.length - 1) {
				onSelectImage(selectedImageIndex + 1);
			}
			if (diff < -threshold && selectedImageIndex > 0) {
				onSelectImage(selectedImageIndex - 1);
			}
			touchStart.current = null;
		},
		[selectedImageIndex, allImages.length, onSelectImage]
	);

	return (
		<Box
			sx={{
				width: '100%',
				height: '100%',
				bgcolor: 'common.white',
				overflowY: 'auto',
				display: 'flex',
				flexDirection: 'column'
			}}
		>
			{/* Content */}
			<Box sx={{ px: 6, pt: 2, pb: 6, flex: 1 }}>
				<Typography
					variant="nav"
					component="h2"
					sx={{
						color: 'common.black',
						fontWeight: 400,
						mb: 4
					}}
				>
					{item.name}
				</Typography>

				<Divider sx={{ mb: 4 }} />

				<Typography
					variant="body-sm"
					component="p"
					sx={{
						color: 'text.secondary',
						...BODY_SM_DETAIL_SX,
						mb: 4
					}}
				>
					{rankingLabel ?? item.description}
				</Typography>

				{item.detail?.fullDescription && (
					<ComponentSharedTextEditor
						blockContent={parsedContent}
						variantBody={nullToUndefined(block?.variantBody)}
						variantHeading1={block?.variantHeading1}
						variantHeading2={block?.variantHeading2}
						variantHeading3={block?.variantHeading3}
						variantHeading4={block?.variantHeading4}
						variantHeading5={block?.variantHeading5}
						variantHeading6={block?.variantHeading6}
						overrides={{
							paragraph: {
								fontSize: '0.875rem',
								lineHeight: '1.375rem',
								letterSpacing: '0.05px',
								color: 'text.secondary',
								'@media (min-width: 768px)': {
									fontSize: '0.875rem'
								}
							},
							listItem: {
								fontSize: '0.875rem',
								'@media (min-width: 768px)': {
									fontSize: '0.875rem'
								}
							},
							heading: {
								mb: 1
							}
						}}
					/>
				)}

				{/* Swipeable image / video */}
				<Box
					role="group"
					aria-roledescription="Mediengalerie"
					aria-label={`Medium ${selectedImageIndex + 1} von ${allImages.length}`}
					onTouchStart={handleImageTouchStart}
					onTouchEnd={handleImageTouchEnd}
					sx={{
						position: 'relative',
						width: '100%',
						aspectRatio: current?.videoUrl ? '16 / 9' : '1 / 1',
						bgcolor: 'common.white',
						mb: 2,
						touchAction: 'pan-y pinch-zoom',
						cursor: allImages.length > 1 ? 'grab' : 'default'
					}}
				>
					{current && (
						<Box
							key={current.videoUrl ?? current.src}
							sx={{
								position: 'absolute',
								inset: 0,
								width: '100%',
								height: '100%'
							}}
						>
							{current.videoUrl ? (
								(() => {
									const ytId = getYouTubeId(current.videoUrl);
									return ytId ? (
										<Box
											component="iframe"
											src={`https://www.youtube.com/embed/${ytId}?rel=0`}
											title={current.alt ?? 'Video'}
											allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
											allowFullScreen
											sx={{
												position: 'absolute',
												inset: 0,
												width: '100%',
												height: '100%',
												border: 'none'
											}}
										/>
									) : (
										<video
											src={current.videoUrl}
											controls
											playsInline
											style={{
												position: 'absolute',
												inset: 0,
												width: '100%',
												height: '100%',
												objectFit: 'contain'
											}}
										>
											<track kind="captions" srcLang="de" label="Deutsch" />
										</video>
									);
								})()
							) : (
								<StrapiImageRenderer
									src={current.src}
									imageData={item.imageData}
									host={item.host}
									fill
									sizes="(max-width: 600px) 100vw, 420px"
									alt={current.alt ?? ''}
									imageFit="contain"
								/>
							)}
						</Box>
					)}
					<Typography aria-live="polite" aria-atomic="true" sx={visuallyHidden}>
						{`Medium ${selectedImageIndex + 1} von ${allImages.length}`}
					</Typography>
				</Box>

				{/* Image navigation */}
				{allImages.length > 1 && (
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							gap: 2,
							mb: 8
						}}
					>
						<IconButton
							onClick={() => onSelectImage(selectedImageIndex - 1)}
							disabled={selectedImageIndex === 0}
							aria-label="Vorheriges Bild"
							size="small"
							sx={{
								color: 'common.black',
								'&:disabled': { color: 'softGrey.main' },
								'&:focusVisible': {
									outline: '2px solid',
									outlineColor: 'primary.main',
									outlineOffset: 2
								}
							}}
						>
							<ChevronLeftIcon fontSize="small" />
						</IconButton>
						<Box
							aria-hidden="true"
							sx={{
								display: 'flex',
								gap: '0.5rem'
							}}
						>
							{allImages.map((img, i) => (
								<Box
									key={img.videoUrl ?? img.src}
									sx={{
										width: 8,
										height: 8,
										borderRadius: isVideoEntry(img) ? '50%' : '2px',
										bgcolor: i === selectedImageIndex ? 'common.black' : 'softGrey.main',
										transition: 'background-color 0.2s ease'
									}}
								/>
							))}
						</Box>
						<IconButton
							onClick={() => onSelectImage(selectedImageIndex + 1)}
							disabled={selectedImageIndex === allImages.length - 1}
							aria-label="Nächstes Bild"
							size="small"
							sx={{
								color: 'common.black',
								'&:disabled': { color: 'softGrey.main' },
								'&:focusVisible': {
									outline: '2px solid',
									outlineColor: 'primary.main',
									outlineOffset: 2
								}
							}}
						>
							<ChevronRightIcon fontSize="small" />
						</IconButton>
					</Box>
				)}

				{/* Meta fields */}
				{(item.detail?.trainingCompany || item.detail?.guild || item.detail?.material) && (
					<Box
						component="dl"
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: 3,
							m: 0
						}}
					>
						{item.detail?.trainingCompany && (
							<MetaRow label="Ausbildungsbetrieb:" value={item.detail.trainingCompany} />
						)}
						{item.detail?.guild && <MetaRow label="Tischler-Innung:" value={item.detail.guild} />}
						{item.detail?.material && <MetaRow label="Material:" value={item.detail.material} />}
					</Box>
				)}
			</Box>
		</Box>
	);
};

export default CompetitionDetailMobile;
